import { diContainer } from '../DIContainer';
import { AppStorageKey } from '../types';
import User from '../models/User';
import Role from '../models/Role';
import Product from '../models/Product';
import ProductCategory from '../models/ProductCategory';
import ProductIngredient from '../models/ProductIngredient';
import TaxRate from '../models/TaxRate';
import Order from '../models/Order';
import OrderItemComment from '../models/OrderItemComment';
import Client from '../models/Client';
import Sector from '../models/Sector';
import Table from '../models/Table';
import Grid from '../models/Grid';
import Button from '../models/Button';
import Shift from '../models/Shift';
import CashRegister from '../models/CashRegister';
import CashRegisterTransaction from '../models/CashRegisterTransaction';
import Session from '../models/Session';
import Shortcut from '../models/Shortcut';
import UserTransaction from '../models/UserTransaction';
import Pricelist from '../models/Pricelist';
import Notification from '../models/Notification';
import KitchenTicket from '../models/KitchenTicket';
import Terminal from '../models/Terminal';
import TableSession from '../models/TableSession';
import BeachBooking from '../models/BeachBooking';
import BeachZone from '../models/BeachZone';
import OrderTicket from '../models/OrderTicket';
import OrderRequest from '../models/OrderRequest';
import AppService from './AppService';
import OrderService from './OrderService';
import { apiService } from './ApiService';
import brandTheme from './BrandThemeService';
import { printService } from '../print';
import { fiscalService, fiscalServiceLegacy } from '../fiscal';
import { connectionService } from './ConnectionService';
import { websocketsService } from './WebsocketsService';
import { AppEventBackupService } from './AppEventBackupService';
import Store from '../wrappers/Store';
import Locale from '../wrappers/Locale';
import { I18n } from '../wrappers/I18n';
import { Settings } from '../wrappers/Settings';
import * as bcrypt from 'bcryptjs';
import localNetworkService from './LocalNetworkService';
import TerminalService from './TerminalService';
import moment from 'moment-timezone';
const TenantService = {
    /**
     * Register the Tenant and store the access token to TokenService.
     **/
    async register(form) {
        const appStorage = diContainer.resolve('appStorage');
        const isAdmin = await appStorage.local().getItem(AppStorageKey.backoffice, null); // app-scoped key: explicit null prefix (see the AppService.initializeAdminApp write)
        try {
            const response = await apiService.post('/register', form, { timeout: 600000 });
            await this.setToken(response.data.access_token);
            await this.setTenant(response.data.tenant);
            if (isAdmin)
                await this.loadAdminData();
            else
                await this.loadData(true);
            return response;
        }
        catch (error) {
            throw error;
        }
    },
    /**
     * Login the Tenant and store the access token to TokenService.
     **/
    async login(form) {
        const appStorage = diContainer.resolve('appStorage');
        const isAdmin = await appStorage.local().getItem(AppStorageKey.backoffice, null); // app-scoped key: explicit null prefix (see the AppService.initializeAdminApp write)
        // If offline re-check connection and if still offline perform offline login
        if (!Store.get('isOnline')) {
            const isOnline = await connectionService.connectionCheck();
            if (!isOnline) {
                return this.offlineLogin(form);
            }
        }
        try {
            const response = await apiService.post('/login', form);
            await this.setToken(response.data.access_token);
            await this.setTenant(response.data.tenant);
            if (isAdmin)
                await this.loadAdminData();
            else
                await this.loadData(true);
            // store the tenant so that it can be used later on for offline login
            if (response.data.tenant) {
                Store.set('lastTenant', response.data.tenant);
            }
            // toggle impersonate mode
            this.setImpersonate(!!response.data.impersonate);
            // set terminal
            const terminalId = await TerminalService.getId();
            if (!terminalId && response.data.cash_register?.terminal) {
                TerminalService.setTerminal(response.data.cash_register?.terminal, response.data.cash_register);
            }
            // sync data if needed
            if (response.data.tenant.venue) {
                await AppService.sync();
            }
            brandTheme.applyFromLocale();
            return response;
        }
        catch (error) {
            await this.removeToken();
            await this.removeTenant();
            throw error;
        }
    },
    /**
     * Perform offline login
     */
    async offlineLogin(form) {
        const lastLoggedTenant = Store.get('lastTenant');
        if (!lastLoggedTenant) {
            const errors = { email: [I18n.t('connection.internet_offline_message_not_allowed')] };
            form.$errors = errors;
            throw errors;
        }
        const email = 'data' in form ? form.data.email : form.email;
        const password = 'data' in form ? form.data.password : form.password;
        let credentialsValid = lastLoggedTenant.email === email && bcrypt.compareSync(password, lastLoggedTenant.password);
        if (credentialsValid) {
            await this.setTenant(lastLoggedTenant);
            brandTheme.applyFromLocale();
            return {
                data: { tenant: lastLoggedTenant }
            };
        }
        else {
            const errors = { email: [I18n.t('validation.credentials')] };
            form.$errors = errors;
            throw errors;
        }
    },
    /**
     * Logout the current Tenant and remove the token from storage.
     *
     * Will also remove `Authorization Tenant <token>` header from future requests.
     */
    async logout() {
        if (Store.get('isOnline')) {
            apiService.post('/logout', {});
        }
        Store.set('curOrder', null);
        Store.set('pendingOrderData', null);
        await this.removeToken();
        await this.removeTenant();
        brandTheme.clear();
    },
    async loadData(isInitialLoad) {
        if (!Store.get('isOnline'))
            return;
        const params = {};
        const pkey = await this.getPkey();
        if (!pkey || pkey === 'null') {
            params.include_key = true;
        }
        if (isInitialLoad) {
            params.initial = true;
        }
        const ordersLastUpdatedAt = Order.getLastUpdatedAt();
        const productsLastUpdatedAt = Product.getLastUpdatedAt();
        params.orders_last_updated_at = ordersLastUpdatedAt ? ordersLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : undefined;
        params.products_last_updated_at = productsLastUpdatedAt ? productsLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : undefined;
        // Unsynced data
        const orders = Order.all().filter(order => !order.id && order.items.length > 0);
        const userTransactions = UserTransaction.all().filter(transaction => !transaction.id);
        const cashRegisterTransactions = CashRegisterTransaction.all().filter(transaction => !transaction.id);
        const sessions = Session.all().filter(session => !session.id);
        const kitchenTickets = KitchenTicket.all().filter(kt => !kt.id);
        try {
            const response = await apiService.get('/load', { params, timeout: 60000 });
            if (isInitialLoad) {
                // leave only unsynced data in store
                Order.create({ data: orders.map((item) => item.$toJson()) });
                UserTransaction.create({ data: userTransactions.map((item) => item.$toJson()) });
                CashRegisterTransaction.create({ data: cashRegisterTransactions.map((item) => item.$toJson()) });
                Session.create({ data: sessions.map((item) => item.$toJson()) });
                KitchenTicket.create({ data: kitchenTickets.map(item => item.$toJson()) });
                // reset products and notifications
                Product.create({ data: [] });
                Notification.create({ data: [] });
            }
            // insert fresh data coming from server
            Order.insert({ data: response.data.orders });
            UserTransaction.insert({ data: response.data.user_transactions });
            CashRegisterTransaction.insert({ data: response.data.cash_register_transactions });
            User.insert({ data: response.data.users });
            Session.insert({ data: response.data.sessions });
            KitchenTicket.insert({ data: response.data.kitchen_tickets });
            Product.insert({ data: response.data.products });
            Notification.insert({ data: response.data.unread_notifications });
            User.create({ data: response.data.users });
            Role.create({ data: response.data.roles });
            ProductCategory.create({ data: response.data.product_categories });
            ProductIngredient.create({ data: response.data.product_ingredients });
            TaxRate.create({ data: response.data.tax_rates });
            Shift.create({ data: [response.data.shift] });
            CashRegister.create({ data: response.data.cash_registers });
            OrderItemComment.create({ data: response.data.order_item_comments });
            Client.create({ data: response.data.clients });
            Table.create({ data: response.data.tables });
            Sector.create({ data: response.data.sectors });
            Grid.create({ data: response.data.grids });
            Button.create({ data: response.data.buttons });
            Shortcut.create({ data: response.data.shortcuts });
            Pricelist.create({ data: response.data.pricelists });
            Terminal.create({ data: response.data.terminals });
            if (response.data.table_sessions) {
                if (isInitialLoad) {
                    TableSession.create({ data: response.data.table_sessions });
                }
                else {
                    TableSession.insert({ data: response.data.table_sessions });
                }
            }
            if (response.data.beach_bookings) {
                if (isInitialLoad) {
                    BeachBooking.create({ data: response.data.beach_bookings });
                }
                else {
                    BeachBooking.insert({ data: response.data.beach_bookings });
                }
            }
            if (response.data.beach_zones) {
                if (isInitialLoad) {
                    BeachZone.create({ data: response.data.beach_zones });
                }
                else {
                    BeachZone.insert({ data: response.data.beach_zones });
                }
            }
            if (response.data.order_tickets) {
                if (isInitialLoad) {
                    OrderTicket.create({ data: response.data.order_tickets });
                }
                else {
                    OrderTicket.insert({ data: response.data.order_tickets });
                }
            }
            if (response.data.order_requests) {
                if (isInitialLoad) {
                    OrderRequest.create({ data: response.data.order_requests });
                }
                else {
                    OrderRequest.insert({ data: response.data.order_requests });
                }
            }
            if (response.data.pkey) {
                this.setPkey(response.data.pkey);
            }
            Store.set('tenant', response.data.tenant);
            Store.set('workingDays', response.data.working_days);
            OrderService.setLatestNumber(response.data.latest_order_number);
            OrderService.setLatestInternalNumber(response.data.latest_order_internal_number);
            if (response.data.latest_order_date) {
                OrderService.setLatestOrderDate(response.data.latest_order_date);
            }
            const toNumber = (id) => !id || isNaN(Number(id)) ? null : Number(id);
            const terminalId = toNumber(await TerminalService.getId());
            const terminal = Terminal.find(terminalId) || null;
            Store.set('terminal', terminal);
            const cashRegisterId = toNumber(await TerminalService.getCashRegisterId());
            Store.set('cashRegister', CashRegister.find(cashRegisterId) || null);
            // if inconsistency found, overwrite cash register with one from terminal
            if (terminal && cashRegisterId !== toNumber(terminal?.cash_register_id)) {
                await TerminalService.setCashRegister(terminal?.$toJson());
            }
            Store.set('modules', response.data.modules || {});
            Store.set('integrations', response.data.integrations || {});
            Store.set('ordersHubActive', response.data.ordersHubActive ?? false);
            const appStorage = diContainer.resolve('appStorage');
            appStorage.local().setItem(AppStorageKey.dataLoadedAt, moment().format('YYYY-MM-DD HH:mm:ss'));
            await this.setUpTestUser();
        }
        catch (error) {
            throw error;
        }
    },
    async loadAdminData() {
        const response = await apiService.get('/load/admin');
        TaxRate.create({ data: response.data.tax_rates });
        CashRegister.create({ data: response.data.cash_registers });
        Notification.insert({ data: response.data.unread_notifications });
        Product.create({ data: response.data.products });
        Client.create({ data: response.data.clients });
        Sector.create({ data: response.data.sectors });
        Table.create({ data: response.data.tables });
        User.create({ data: response.data.users });
        Pricelist.create({ data: response.data.pricelists });
        // The backoffice logs in without re-running /initialize, so the maps
        // gating admin menu items must be hydrated here or they stay empty
        // until a full page refresh. Guarded on presence for deploy-order
        // safety: a backend without the e-dispatch branch omits these maps
        // from /load/admin entirely, and an unconditional write here would
        // wipe out the values /initialize already hydrated (hiding unrelated
        // menu entries like pricelists/scales/beach/delivery app-wide).
        if (response.data.modules)
            Store.set('modules', response.data.modules);
        if (response.data.integrations)
            Store.set('integrations', response.data.integrations);
    },
    /**
     * Set Tenant
     */
    async setTenant(tenant, startServices = true) {
        // Reset the backup-poll watermark on every tenant change (login / logout /
        // switch). The watermark is module-scoped and survives across re-login in
        // the same process, so without this the next session could poll with the
        // previous tenant's server clock.
        AppEventBackupService.reset();
        Store.set('tenant', tenant);
        Store.set('isRetail', tenant?.app === 'allegra');
        // The offline-login copy of this tenant follows its status (markInactive keeps them in
        // step the other way): a reactivated tenant must not log in offline locked.
        const lastTenant = Store.get('lastTenant');
        if (tenant && lastTenant?.id === tenant.id && lastTenant.status !== tenant.status) {
            Store.set('lastTenant', { ...lastTenant, status: tenant.status });
        }
        if (!tenant) {
            apiService.removeTenantHeader();
            apiService.removeCashRegisterHeader();
            apiService.unmountInterceptor401();
            apiService.unmountInterceptor402();
        }
        else {
            apiService.setTenantHeader(await this.getToken());
            apiService.setCashRegisterHeader(await TerminalService.getCashRegisterId());
            apiService.mountInterceptor401();
            apiService.mountInterceptor402();
            if (tenant.venue) {
                await Locale.set(tenant.venue.locale);
                await Locale.setLanguage(tenant.venue.language);
                await Locale.setCurrency(tenant.venue.currency);
            }
        }
        if (startServices) {
            this.startTenantServices();
        }
    },
    startTenantServices() {
        const tenant = Store.get('tenant');
        websocketsService.reload();
        localNetworkService.reload();
        if (tenant?.venue) {
            fiscalService.init();
            fiscalServiceLegacy.init();
            printService.init();
        }
    },
    /**
     * Remove Tenant
     */
    async removeTenant() {
        await this.setTenant(null);
    },
    /**
     * Whether the tenant is one aria-api refuses (402, CheckSubscriptionsPlans): expired or
     * disabled. Any other value — an active status or none at all — keeps the till working.
     */
    isInactive(tenant) {
        return tenant?.status === 'expired' || tenant?.status === 'disabled';
    },
    /**
     * aria-api refused the tenant (402) with its status: record it on the stored tenant — the
     * persisted copy the app boots from — and on the offline-login copy, so the lock holds
     * across a restart and offline. A tenant read from the server (/initialize, /me, /login)
     * is the only thing that sets it back.
     */
    markInactive(status) {
        const tenant = Store.get('tenant');
        if (!tenant)
            return;
        const inactiveStatus = status === 'disabled' ? 'disabled' : 'expired';
        if (tenant.status !== inactiveStatus) {
            Store.set('tenant', { ...tenant, status: inactiveStatus });
        }
        const lastTenant = Store.get('lastTenant');
        if (lastTenant?.id === tenant.id && lastTenant.status !== inactiveStatus) {
            Store.set('lastTenant', { ...lastTenant, status: inactiveStatus });
        }
    },
    /** The message an inactive tenant is shown (the lock, a refused receipt). */
    inactiveMessageKey(tenant) {
        return tenant?.status === 'disabled'
            ? 'subscription.disabled_contact_support'
            : 'subscription.expired_contact_support';
    },
    /**
     * Set impersonate mode
     */
    setImpersonate(impersonate) {
        Store.set('impersonate', impersonate);
    },
    async setUpTestUser() {
        const tenant = Store.get('tenant');
        if (!tenant?.email || !Settings.env.TEST_USERS?.includes(tenant.email))
            return;
        const curTerminal = await Terminal.current();
        const cashRegisterTerminal = Terminal.all().find(t => t.isCashRegister());
        if (!curTerminal && cashRegisterTerminal) {
            await TerminalService.setTerminal(cashRegisterTerminal, cashRegisterTerminal.getCashRegister());
        }
        Store.set('modules', {
            'inventory_accounting': false,
            'einvoices': true,
            'ordering_devices': true,
            'kds': true,
            'product_buttons': true,
            'product_ingredients': true,
            'pricelists': true,
            'delivery': true,
        });
        Store.set('ordersHubActive', true);
    },
    /**
     * Get Tenant Token
     */
    async getToken() {
        const appStorage = diContainer.resolve('appStorage');
        return appStorage.local().getItem(AppStorageKey.tnToken, null);
    },
    /**
     * Set Tenant Token
     */
    async setToken(accessToken) {
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().setItem(AppStorageKey.tnToken, accessToken, null);
    },
    /**
     * Remove Tenant Token
     */
    async removeToken() {
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().deleteItem(AppStorageKey.tnToken, null);
    },
    /**
     * Get Tenant Private Key
     */
    async getPkey() {
        const appStorage = diContainer.resolve('appStorage');
        return appStorage.local().getItem(AppStorageKey.tnPKey);
    },
    /**
     * Set Tenant Private Key
     */
    async setPkey(pkey) {
        const appStorage = diContainer.resolve('appStorage');
        appStorage.local().setItem(AppStorageKey.tnPKey, pkey);
    },
};
export default TenantService;
export { TenantService };
//# sourceMappingURL=TenantService.js.map